// Angular
import { Directive, ElementRef, HostListener, OnInit, Renderer2, input, inject, effect } from '@angular/core';

/**
 * Directive to lazy load image and add fallack image on image error load
 * @export
 * @class ImageLoaderDirective
 * @implements {OnInit}
 * @implements {AfterContentInit}
 */
@Directive({
    // eslint-disable-next-line @angular-eslint/directive-selector
    selector: 'img[imageLoader]', exportAs: 'imageLoader',
})
export class ImageLoaderDirective implements OnInit {
    private el = inject<ElementRef<HTMLImageElement>>(ElementRef);
    private renderer = inject(Renderer2);

    // INPUTS
    public readonly src = input.required<string>();
    public readonly errorSrc = input<string>('assets/media/placholder/no-logo.png');

    // Private
    private spinnerElement: HTMLElement | null = null;
    private alreadyTriedError = false;

    constructor() {
        effect(() => {
            const newSrc = this.src();
            if (!newSrc) return;

            this.alreadyTriedError = false;
            this.renderer.addClass(this.el.nativeElement, 'opacity-0');

            this.removeSpinner();
            this.createSpinner();

            this.renderer.setAttribute(this.el.nativeElement, 'src', newSrc);
        });
    }

    /**
     * On Init
     * @memberof ImageLoaderDirective
     */
    ngOnInit(): void {
        this.renderer.addClass(this.el.nativeElement, 'opacity-0');
        this.createSpinner();
        this.renderer.setAttribute(this.el.nativeElement, 'src', this.src());
        this.renderer.setAttribute(this.el.nativeElement, 'loading', 'lazy');
    }

    @HostListener('load')
    public onLoad(): void {
        this.removeSpinner();
        this.renderer.removeClass(this.el.nativeElement, 'opacity-0');
    }

    @HostListener('error')
    public onError(): void {
        this.removeSpinner();
        this.renderer.removeClass(this.el.nativeElement, 'opacity-0');
        if (!this.alreadyTriedError) {
            this.alreadyTriedError = true;
            this.renderer.setAttribute(this.el.nativeElement, 'src', this.errorSrc());
        }
    }

    /**
     * Create Spinner
     * @memberOf ImageLoaderDirective
     */
    private createSpinner(): void {
        const parent = this.renderer.parentNode(this.el.nativeElement);
        if (!parent) return;
        this.spinnerElement = this.renderer.createElement('div');
        const containerClass = 'position-absolute top-0 start-0 w-100 h-100 d-flex justify-content-center align-items-center z-index-1';
        this.renderer.setAttribute(this.spinnerElement, 'class', containerClass);

        const spinner = this.renderer.createElement('div');
        this.renderer.setAttribute(spinner, 'class', 'spinner-border spinner-border-sm text-primary');
        this.renderer.setAttribute(spinner, 'role', 'status');

        const span = this.renderer.createElement('span');
        this.renderer.setAttribute(span, 'class', 'visually-hidden');
        this.renderer.appendChild(span, this.renderer.createText('Loading...'));

        this.renderer.appendChild(spinner, span);
        this.renderer.appendChild(this.spinnerElement, spinner);
        this.renderer.appendChild(parent, this.spinnerElement);
    }

    /**
     * Remove Spinner
     * @memberOf ImageLoaderDirective
     */
    private removeSpinner(): void {
        if (this.spinnerElement) {
            const parent = this.renderer.parentNode(this.el.nativeElement);
            if (parent && this.spinnerElement) {
                this.renderer.removeChild(parent, this.spinnerElement);
            }
            this.spinnerElement = null;
        }
    }
}